import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { AppBridgeProvider } from './components/AppBridgeProvider';
import apiClient from './utils/apiClient';
import LandingPage from './components/LandingPage';
import VortexAIAdvisory from './components/VortexAIAdvisory';
import Dashboard from './components/Dashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AdminPanel from './components/AdminPanel';
import '@/App.css';

function AppContent() {
  const [token, setToken] = useState(localStorage.getItem('shopify_token'));
  const [loading, setLoading] = useState(true);
  const [shopDomain, setShopDomain] = useState(localStorage.getItem('shop_domain'));
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Helper functions - defined before useEffect to avoid hoisting issues
  const login = (newToken, domain) => {
    setToken(newToken);
    setShopDomain(domain);
    localStorage.setItem('shopify_token', newToken);
    localStorage.setItem('shop_domain', domain);
    toast.success('Successfully connected to your Shopify store!');
    setLoading(false);
  };

  const logout = () => {
    setToken(null);
    setShopDomain(null);
    localStorage.removeItem('shopify_token');
    localStorage.removeItem('shop_domain');
    setLoading(false);
  };

  const testTokenValidity = async () => {
    try {
      await apiClient.get('/health');
      setLoading(false);
    } catch (error) {
      console.error('Token validation failed:', error);
      logout();
    }
  };

  const initiateOAuth = async (shop) => {
    try {
      const response = await apiClient.get(`/shopify/install?shop=${encodeURIComponent(shop)}`);
      window.location.href = response.data.install_url;
    } catch (error) {
      console.error('OAuth initiation failed:', error);
      setLoading(false);
      toast.error('Failed to connect. Please install from the Shopify App Store.');
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host');
    const shop = urlParams.get('shop');
    const urlToken = urlParams.get('token');

    // Case 1: OAuth callback - received token from backend after successful OAuth (legacy flow)
    if (urlToken && shop) {
      login(urlToken, shop);
      window.history.replaceState({}, document.title, '/dashboard');
      return;
    }

    // Case 2: Embedded in Shopify Admin (has both host and shop)
    // Modern flow: App Bridge provides session tokens
    // Backend handles token exchange automatically when needed
    if (host && shop) {
      setIsEmbedded(true);
      setShopDomain(shop);
      setLoading(false);
      return;
    }
    
    // Case 3: Shop param exists (from Shopify App Store) but not embedded
    // This triggers the legacy OAuth flow for non-embedded installs
    if (shop && !token) {
      initiateOAuth(shop);
      return;
    }
    
    // Case 4: Have existing token in standalone mode - validate it
    if (token) {
      testTokenValidity();
    } else {
      // No shop param, no token - show landing page
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Embedded mode - always show dashboard
  if (isEmbedded) {
    return (
      <div className="App">
        <Dashboard shopDomain={shopDomain} onLogout={logout} isEmbedded={true} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Standalone mode - normal routing
  return (
    <div className="App">
      <Routes>
        <Route 
          path="/" 
          element={
            token ? 
              <Navigate to="/dashboard" replace /> : 
              <LandingPage />
          } 
        />
        <Route 
          path="/vortex-ai-advisory" 
          element={<VortexAIAdvisory />} 
        />
        <Route 
          path="/dashboard" 
          element={
            token ? 
              <Dashboard shopDomain={shopDomain} onLogout={logout} isEmbedded={false} /> : 
              <Navigate to="/" replace />
          } 
        />
        <Route 
          path="/privacy-policy" 
          element={<PrivacyPolicy />} 
        />
        <Route 
          path="/terms-of-service" 
          element={<TermsOfService />} 
        />
        <Route 
          path="/admin" 
          element={<AdminPanel />} 
        />
        <Route 
          path="*" 
          element={<Navigate to="/" replace />} 
        />
      </Routes>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppBridgeProvider>
        <AppContent />
      </AppBridgeProvider>
    </BrowserRouter>
  );
}

export default App;
